import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { isHistoryDetailsResponse, ResourceType } from "@common/types/auditLog";
import {
    getShowRevertedParam,
    type HistoryRow,
} from "@components/history/views/historyViewConfig";
import { Link } from "@design-system/Link/Link";
import { Loader } from "@design-system/Loader/Loader";
import { PanelEmptyMessage } from "@design-system/PanelEmptyMessage/PanelEmptyMessage";
import { buildTaskOpenUrl } from "@hooks/useOpenWorkRow";
import { toWorkActivityChangeLines } from "./workActivityChangeLines";
import styles from "./WorkActivity.module.scss";

const CHANGES_LIMIT = 50;

interface WorkActivityChangesProps {
    row: HistoryRow;
    closeId: string;
    workspaceId: string;
}

/**
 * The changes behind one activity line, each task with its before and after
 * value, as the Activity page's details list them, or each field the work
 * item's own edit changed. Created tasks list by name alone. Task names open
 * the task in the client's workspace, in a new tab.
 */
export const WorkActivityChanges = ({
    row,
    closeId,
    workspaceId,
}: Readonly<WorkActivityChangesProps>) => {
    const { data, isPending, isError } = useGetHistoryDetailsQuery({
        date: row.date,
        sourceType: row.sourceType,
        resourceType: row.resourceType,
        actorId: row.actorId,
        changeTypes: row.changeTypes,
        fields: row.fields,
        sourceIds: row.historyItem.sourceIds,
        showReverted: getShowRevertedParam(row),
        relatedResource: { type: ResourceType.CLOSE, id: closeId },
        page: 1,
        limit: CHANGES_LIMIT,
    });

    if (isPending) {
        return <Loader inline />;
    }
    if (isError || !isHistoryDetailsResponse(data)) {
        return <PanelEmptyMessage>Failed to load changes</PanelEmptyMessage>;
    }

    const changes = data.data.flatMap(toWorkActivityChangeLines);
    const hiddenCount = data.total - data.data.length;

    return (
        <ul className={styles.changes}>
            {changes.map((change) => (
                <li key={change.id} className={styles.change}>
                    {change.taskId !== undefined && (
                        <Link
                            className={styles.taskLink}
                            size="xs"
                            href={buildTaskOpenUrl({
                                workspaceId,
                                taskId: change.taskId,
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className={styles.changeTitle}>
                                {change.title}
                            </span>
                        </Link>
                    )}
                    {change.taskId === undefined && change.title && (
                        <span className={styles.changeTitle}>
                            {change.title}
                        </span>
                    )}
                    {change.values && (
                        <span className={styles.changeValues}>
                            {change.values.fieldLabel &&
                                `${change.values.fieldLabel}: `}
                            {change.values.previous !== undefined && (
                                <>
                                    {change.values.previous}
                                    <span aria-hidden> → </span>
                                    <span className={styles.visuallyHidden}>
                                        {" "}
                                        to{" "}
                                    </span>
                                </>
                            )}
                            {change.values.modified}
                        </span>
                    )}
                </li>
            ))}
            {hiddenCount > 0 && (
                <li className={styles.changeValues}>
                    and {hiddenCount} more on the Activity page
                </li>
            )}
        </ul>
    );
};
